import { convexQuery } from '@convex-dev/react-query'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { IconApartmentBuildingOutline18 } from 'nucleo-ui-outline-18'
import { DocumentEditor } from '~/components/documents/editor/document-editor'
import { Button } from '~/components/ui/button'
import { EmptyState } from '~/components/ui/empty-state'
import { Spinner } from '~/components/ui/spinner'
import { api } from '~convex/_generated/api'
import type { Id } from '~convex/_generated/dataModel'

/**
 * `/$workspaceSlug/documents/$documentId/edit` — the full-screen document
 * editor.
 *
 * A child of the document's own URL rather than a sibling: `/documents/$id` is
 * the lifecycle page (status, public link, send, revoke) and this is where the
 * document is composed, so `/edit` is the verb applied to that same resource.
 * They cannot share one path, which is what the route rename had to resolve.
 *
 * The trailing underscore on `$workspaceSlug_` keeps the URL segment while
 * opting OUT of the workspace layout: that layout paints the icon rail, side
 * nav and content card around its outlet, and this surface needs the whole
 * viewport for a config column plus a full-width preview. Same reasoning as
 * Like the client portal, it resolves
 * its own workspace.
 */
export const Route = createFileRoute('/_authenticated/$workspaceSlug_/documents/$documentId/edit')({
  component: RouteComponent,
  ssr: false,
})

function RouteComponent() {
  const { t } = useLingui()
  const { documentId, workspaceSlug } = Route.useParams()
  const { data: workspace } = useQuery(
    convexQuery(api.workspaces.getBySlug, { slug: workspaceSlug })
  )

  if (workspace === undefined) {
    return (
      <div className="flex h-full items-center justify-center bg-canvas">
        <Spinner />
      </div>
    )
  }

  if (workspace === null) {
    return (
      <div className="flex h-full items-center justify-center bg-canvas">
        <EmptyState
          description={t`This workspace doesn't exist or you don't have access to it.`}
          icon={IconApartmentBuildingOutline18}
          title={t`Workspace not found`}
        >
          <Button render={<Link to="/home" />}>
            <Trans>Go to my workspace</Trans>
          </Button>
        </EmptyState>
      </div>
    )
  }

  return (
    <DocumentEditor
      documentId={documentId as Id<'documents'>}
      workspace={workspace}
      workspaceSlug={workspaceSlug}
    />
  )
}
